/**
 * Mordeczka.eu Admin - Images Gallery Page
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Select,
  Button,
  Upload,
  Input,
  Space,
  Typography,
  Popconfirm,
  message,
  Image as AntImage,
  Row,
  Col,
  Empty,
  Alert,
  Modal,
} from "antd";
import { UploadOutlined, LinkOutlined, DeleteOutlined, FolderOutlined, EditOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import { imageCategoriesApi, imagesApi } from "../services/api";
import type { Image } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:9006";

const CATEGORY_ALL = "";

export function ImagesPage() {
  const queryClient = useQueryClient();
  const { canEdit, canDelete } = usePermissions();
  const [categoryFilter, setCategoryFilter] = useState<string>(CATEGORY_ALL);
  const [uploadCategoryId, setUploadCategoryId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [uploadUrl, setUploadUrl] = useState("");
  const [uploadUrlCategory, setUploadUrlCategory] = useState<string | undefined>(undefined);
  const [uploadUrlTitle, setUploadUrlTitle] = useState("");
  const [uploadFileTitle, setUploadFileTitle] = useState("");
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ["image-categories-public"],
    queryFn: () => imageCategoriesApi.listPublic(),
    retry: false,
  });

  const categories = categoriesData ?? [];

  const { data: imagesData, isLoading } = useQuery({
    queryKey: ["images", categoryFilter, page],
    queryFn: () =>
      imagesApi.list({
        page,
        page_size: 24,
        category_id: categoryFilter === CATEGORY_ALL ? undefined : categoryFilter,
      }),
  });

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      title,
      category_id,
    }: {
      file: File;
      title?: string;
      category_id?: string;
    }) => imagesApi.upload(file, title, category_id),
    onSuccess: () => {
      message.success("Obraz dodany");
      queryClient.invalidateQueries({ queryKey: ["images"] });
      setUploadFileTitle("");
    },
    onError: () => message.error("Nie udało się dodać obrazu"),
  });

  const uploadFromUrlMutation = useMutation({
    mutationFn: async ({
      url,
      title,
      category_id,
    }: {
      url: string;
      title?: string;
      category_id?: string;
    }) => imagesApi.uploadFromUrl(url, title, category_id),
    onSuccess: () => {
      message.success("Obraz dodany z URL");
      queryClient.invalidateQueries({ queryKey: ["images"] });
      setUploadUrl("");
      setUploadUrlTitle("");
      setUploadUrlCategory(undefined);
    },
    onError: (err: Error) =>
      message.error(err?.message ?? "Nie udało się dodać obrazu z URL"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title?: string }) =>
      imagesApi.update(id, { title }),
    onSuccess: () => {
      message.success("Nazwa obrazka zaktualizowana");
      queryClient.invalidateQueries({ queryKey: ["images"] });
      setEditingImageId(null);
      setEditingTitle("");
    },
    onError: () => message.error("Nie udało się zaktualizować nazwy"),
  });

  const deleteMutation = useMutation({
    mutationFn: imagesApi.delete,
    onSuccess: () => {
      message.success("Obraz usunięty");
      queryClient.invalidateQueries({ queryKey: ["images"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const imagePreviewUrl = (img: Image) => {
    return `${API_URL}/api/v1/public/images/${img.id}/content`;
  };

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    accept: "image/*",
    beforeUpload: (file) => {
      if (!canEdit) return false;
      uploadMutation.mutate({
        file,
        title: uploadFileTitle.trim() || undefined,
        category_id: uploadCategoryId,
      });
      return false;
    },
  };

  const handleEditImage = (img: Image) => {
    setEditingImageId(img.id);
    setEditingTitle(img.title || img.filename);
  };

  const handleSaveEdit = () => {
    if (!editingImageId) return;
    updateMutation.mutate({
      id: editingImageId,
      title: editingTitle.trim() || undefined,
    });
  };

  const handleCancelEdit = () => {
    setEditingImageId(null);
    setEditingTitle("");
  };

  const handleUploadFromUrl = () => {
    if (!uploadUrl.trim()) {
      message.warning("Wprowadź URL");
      return;
    }
    uploadFromUrlMutation.mutate({
      url: uploadUrl.trim(),
      title: uploadUrlTitle.trim() || undefined,
      category_id: uploadUrlCategory ?? undefined,
    });
  };

  const items = imagesData?.items ?? [];
  const total = imagesData?.total ?? 0;
  const totalPages = imagesData?.total_pages ?? 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Obrazki
        </Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text strong>Filtr listy</Typography.Text>
          <Space wrap align="center">
            <Select
              placeholder="Kategoria obrazków"
              style={{ minWidth: 260 }}
              value={categoryFilter}
              onChange={setCategoryFilter}
              loading={categoriesLoading}
              options={[
                { value: CATEGORY_ALL, label: "Wszystkie kategorie" },
                ...categories.map((c) => ({ value: c.id, label: `${c.name} (${c.slug})` })),
              ]}
            />
          </Space>

          {categoriesError && (
            <Alert
              type="warning"
              message="Nie można załadować kategorii"
              description={
                <span>
                  Sprawdź, czy backend działa (port 9006) i czy wykonałeś migrację:{" "}
                  <code>.\migrate.ps1</code>. Endpoint: GET /api/v1/public/image-categories. Możesz
                  dalej dodawać obrazy bez wyboru kategorii.
                </span>
              }
              showIcon
              action={
                <Button size="small" onClick={() => refetchCategories()}>
                  Ponów
                </Button>
              }
            />
          )}
          {!categoriesError && categories.length === 0 && !categoriesLoading && (
            <Alert
              type="info"
              message="Brak kategorii obrazków"
              description={
                <span>
                  Dodaj kategorie w{" "}
                  <Link to="/image-categories">
                    <Button type="link" size="small" icon={<FolderOutlined />}>
                      Kategorie obrazków
                    </Button>
                  </Link>
                  , potem wróć tutaj — będziesz mógł filtrować i przypisywać obrazki do kategorii.
                </span>
              }
              showIcon
            />
          )}

          {canEdit && (
            <>
              <Typography.Text strong style={{ display: "block", marginTop: 8 }}>
                Dodaj obrazek
              </Typography.Text>
              <Space wrap align="center">
                <Input
                  placeholder="Nazwa obrazka (opcjonalnie)"
                  value={uploadFileTitle}
                  onChange={(e) => setUploadFileTitle(e.target.value)}
                  style={{ width: 240 }}
                />
                <Select
                  placeholder="Zapisz w kategorii (opcjonalnie)"
                  allowClear
                  style={{ minWidth: 240 }}
                  value={uploadCategoryId}
                  onChange={setUploadCategoryId}
                  loading={categoriesLoading}
                  options={categories.map((c) => ({ value: c.id, label: `${c.name} (${c.slug})` }))}
                />
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />} loading={uploadMutation.isPending}>
                    Wybierz plik i dodaj
                  </Button>
                </Upload>
              </Space>

              <Typography.Text strong style={{ display: "block", marginTop: 12 }}>
                Lub pobierz z URL
              </Typography.Text>
              <Space.Compact style={{ maxWidth: 560 }}>
                <Input
                  placeholder="URL obrazka (upload z internetu)"
                  value={uploadUrl}
                  onChange={(e) => setUploadUrl(e.target.value)}
                  onPressEnter={handleUploadFromUrl}
                />
                <Select
                  placeholder="Kategoria"
                  allowClear
                  style={{ width: 180 }}
                  value={uploadUrlCategory}
                  onChange={setUploadUrlCategory}
                  loading={categoriesLoading}
                  options={categories.map((c) => ({
                    value: c.id,
                    label: c.slug,
                  }))}
                />
                <Button
                  icon={<LinkOutlined />}
                  loading={uploadFromUrlMutation.isPending}
                  onClick={handleUploadFromUrl}
                >
                  Pobierz
                </Button>
              </Space.Compact>
              <Input
                placeholder="Tytuł (opcjonalnie)"
                value={uploadUrlTitle}
                onChange={(e) => setUploadUrlTitle(e.target.value)}
                style={{ maxWidth: 400 }}
              />
            </>
          )}
        </Space>
      </Card>

      <Card>
        {items.length === 0 && !isLoading ? (
          <Empty description="Brak obrazków" />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {items.map((img) => (
                <Col xs={24} sm={12} md={8} lg={6} key={img.id}>
                  <Card
                    size="small"
                    cover={
                      <div style={{ height: 140, background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <AntImage
                          src={imagePreviewUrl(img)}
                          alt={img.title ?? img.filename}
                          style={{ maxHeight: 140, objectFit: "contain" }}
                          fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='1'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E"
                        />
                      </div>
                    }
                    actions={
                      canEdit || canDelete
                        ? [
                            ...(canEdit
                              ? [
                                  <Button
                                    key="edit"
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditImage(img)}
                                  />,
                                ]
                              : []),
                            ...(canDelete
                              ? [
                                  <Popconfirm
                                    key="del"
                                    title="Usunąć ten obraz?"
                                    onConfirm={() => deleteMutation.mutate(img.id)}
                                    okText="Usuń"
                                    cancelText="Anuluj"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                    />
                                  </Popconfirm>,
                                ]
                              : []),
                          ]
                        : undefined
                    }
                  >
                    <Card.Meta
                      title={img.title || img.filename}
                      description={
                        <Space direction="vertical" size={0}>
                          {img.file_size != null && (
                            <span>{(img.file_size / 1024).toFixed(1)} KB</span>
                          )}
                          {img.category_slug && (
                            <code style={{ fontSize: 11 }}>{img.category_slug}</code>
                          )}
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
            {totalPages > 1 && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <Space>
                  <Button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Poprzednia
                  </Button>
                  <span>
                    Strona {page} z {totalPages} (łącznie {total})
                  </span>
                  <Button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Następna
                  </Button>
                </Space>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        title="Edytuj nazwę obrazka"
        open={editingImageId !== null}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        okText="Zapisz"
        cancelText="Anuluj"
        confirmLoading={updateMutation.isPending}
      >
        <Input
          placeholder="Nazwa obrazka"
          value={editingTitle}
          onChange={(e) => setEditingTitle(e.target.value)}
          onPressEnter={handleSaveEdit}
          autoFocus
        />
      </Modal>
    </div>
  );
}

export default ImagesPage;
